import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Icon from '../../components/icon/Icon';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import Tabs from '../../components/tabs/Tabs';
import MissingUsdQuotationModal from '../../components/modals/MissingUsdQuotationModal';
import { 
  getProtectorDetail, 
} from '../../services/GuaranteesService';
import { routes } from '../../routes/routePaths';
import { isUsdQuotationAvailable } from '../../utils/usdQuotationCheck';
import ProtectorDetailsBasicSection from './ProtectorDetailsBasicSection';
import ProtectorDetailsContributionsSection from './ProtectorDetailsContributionsSection';
import ProtectorDetailsShareReceiptsSection from './ProtectorDetailsShareReceiptsSection';
import ProtectorDetailsDefaultSection from './ProtectorDetailsDefaultSection';
import ProtectorDetailsLiquidationsSection from './ProtectorDetailsLiquidationsSection';
import { Toast, useToast } from '../../components/toast/toast';
import { normalizeDateOnlyFromApi } from '../../utils/dateOnly';
import PageBreadcrumb from '../../components/PageBreadcrumb/PageBreadcrumb';

interface ProtectorDetailData {
  id: number;
  company_name: string;
  document_type_id: number;
  document_type_name: string;
  legal_entity_type_id: number;
  legal_entity_type_name: string;
  cuit: string;
  email: string;
  meeting_minute_type_id: number;
  meeting_minute_type_name: string;
  minute_number: string;
  registration_date: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const ProtectorsDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [protectorData, setProtectorData] = useState<ProtectorDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingUsdQuotationModalOpen, setMissingUsdQuotationModalOpen] = useState(false);
  const [checkingUsdQuotation, setCheckingUsdQuotation] = useState(false);

  useEffect(() => {
    if (id) {
      fetchProtectorDetail();
    }
  }, [id]);

  useEffect(() => {
    if (loading) return;

    const toastPayload = (location.state as { toast?: { title?: string; description?: string } } | null)
      ?.toast;
    if (!toastPayload?.description) return;

    useToast.error({
      title: toastPayload.title || 'Error',
      description: toastPayload.description,
      id: 'default-toast',
      autoClose: 8000,
      closeButton: true,
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [loading, location.state, location.pathname, navigate]);

  const fetchProtectorDetail = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProtectorDetail(id);
      if (response.success && 'data' in response) {
        const data = response.data;
        setProtectorData({
          ...data,
          registration_date:
            normalizeDateOnlyFromApi(data.registration_date) ||
            data.registration_date ||
            '',
        });
      } else {
        setError('Error al cargar los datos del protector');
      }
    } catch (err) {
      setError('Error al cargar los datos del protector');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.fundsAndProtectors);
  };

  const handleEditClick = () => {
    if (id) {
      navigate(routes.protectorEdit.replace(':id', id));
    }
  };

  const handleNewContribution = async () => {
    if (!id || checkingUsdQuotation) return;

    setCheckingUsdQuotation(true);
    try {
      const hasUsdQuotation = await isUsdQuotationAvailable();
      if (!hasUsdQuotation) {
        setMissingUsdQuotationModalOpen(true);
        return;
      }
      navigate(`/protectores/${id}/aportes/new`);
    } finally {
      setCheckingUsdQuotation(false);
    }
  };

  const handleCloseMissingUsdQuotationModal = () => {
    setMissingUsdQuotationModalOpen(false);
  };

  const handleGoToQuotations = () => {
    setMissingUsdQuotationModalOpen(false);
    navigate(routes.quotations);
  };

  const companyCrumb = protectorData?.company_name?.trim() || 'Detalle protector';
  const breadcrumb = (
    <PageBreadcrumb
      items={[
        { label: 'Inicio', to: routes.dashboard },
        { label: 'Fondos y protectores', to: routes.fundsAndProtectors },
        { label: companyCrumb },
      ]}
    />
  );

  if (loading) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <TableSkeleton rows={10} columns={4} />
      </Card>
      </>
    );
  }

  if (error || !protectorData) {
    return (
      <>
        <div style={{ width: '85vw' }}>{breadcrumb}</div>
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <P type="1" weight="regular" modifier="weak">{error || 'Protector no encontrado'}</P>
        </Card>
      </Card>
      <Toast id="default-toast" position="top-right" />
      </>
    );
  }

  return (
    <>
      <div style={{ width: '85vw' }}>{breadcrumb}</div>
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      <Card
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        style={{ marginBottom: '24px' }}
      >
        <Card display='flex' alignItems='center' gap='16px'>
          <button
            type='button'
            onClick={handleGoBack}
            aria-label="Volver a protectores"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <Icon
              name='chevron_left_outline'
              variant='primary'
              modifier='regular'
              width={24}
              height={24}
              cursor='pointer'
            />
          </button>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Detalle protector
          </H>
        </Card>
      </Card>
      
      <ProtectorDetailsBasicSection 
        protector={protectorData}
        onEditClick={handleEditClick}
      />

      <Tabs
        tabs={[
          {
            label: 'Aportes',
            content: (
              <ProtectorDetailsContributionsSection 
                protectorId={id}
                onNewContribution={handleNewContribution}
              />
            )
          },
          {
            label: 'Cesiones',
            content: (
              <ProtectorDetailsShareReceiptsSection 
                protectorId={id}
              />
            )
          },
          {
            label: 'Movimientos de afrontes y honras',
            content: (
              <ProtectorDetailsDefaultSection 
                protectorId={id}
                cuit={protectorData?.cuit ?? ''}
              />
            )
          },
          {
            label: 'Liquidaciones',
            content: (
              <ProtectorDetailsLiquidationsSection
                protectorId={id}
              />
            )
          }
        ]}
        defaultActiveTab={0}
      />
      <Toast id="default-toast" position="top-right" />
    </Card>
    <MissingUsdQuotationModal
      isOpen={missingUsdQuotationModalOpen}
      onClose={handleCloseMissingUsdQuotationModal}
      onGoToQuotations={handleGoToQuotations}
    />
    </>
  );
};

export default ProtectorsDetails;
